import { useEffect, useRef, useState, type ReactNode } from 'react';
import { personLabelFor, pruneHiddenAnswers } from '../api/v2TakeApi';
import type { DiscoveryAnswers, V2Response, V2TakePayload } from '../api/v2TakeApi';
import { useSaveV2Progress } from '../hooks/useV2TakeAssessment';
import { LandingScreen } from './flowScreens';
import { V2CompletionScreen } from './V2CompletionScreen';
import { V2_LANDING } from './v2Landing';
import { V2DiscoveryScreen } from './V2DiscoveryScreen';
import { V2PillarScreen } from './V2PillarScreen';

/**
 * The MVP 2 client flow (Feature 110): landing → discovery → five pillars →
 * sharing choice.
 *
 * Rendered by `TakeAssessmentPage`, which owns the shell and the transitions
 * and picks this flow when the payload says version 2. Discovery comes first
 * because it is what makes the resulting priorities specific to this household
 * — it never touches the score.
 */

type Step = 'landing' | 'discovery' | number | 'completion';

type V2TakeFlowProps = {
  token: string;
  data: V2TakePayload;
  onCreateAccount: (email: string) => void;
  /** Starts normal sign-in for an existing account (Feature 095 split). */
  onSignIn: () => void;
  /** Lets the page animate between steps without owning the step state. */
  renderStep: (key: string, node: ReactNode) => ReactNode;
};

export function V2TakeFlow({
  token,
  data,
  onCreateAccount,
  onSignIn,
  renderStep,
}: V2TakeFlowProps) {
  // Separate instances so a failed discovery save never shows its error on a
  // pillar screen, and vice versa — each screen reads its own pending/error.
  const saveDiscovery = useSaveV2Progress(token);
  const saveAnswers = useSaveV2Progress(token);

  const [step, setStep] = useState<Step>('landing');
  const [discovery, setDiscovery] = useState<DiscoveryAnswers>({});
  const [answers, setAnswers] = useState<Record<string, V2Response>>({});
  const [subjectName, setSubjectName] = useState('');
  const seededToken = useRef<string | null>(null);

  // Seed from what was already saved, keyed by token: React Router keeps this
  // component instance across links, so a different token must reseed and
  // restart rather than inherit the previous assessment's answers.
  useEffect(() => {
    if (seededToken.current !== token) {
      setDiscovery(data.savedDiscovery ?? {});
      setAnswers(data.savedAnswers ?? {});
      setSubjectName(data.subjectFirstName ?? '');
      setStep('landing');
      seededToken.current = token;
    }
  }, [data, token]);

  const pillars = data.content.pillars;
  const { personLabels } = data.content;
  // The name only exists while D1 says the client answers about someone else.
  const isSupporter = personLabelFor(discovery, personLabels) !== undefined;

  /**
   * Advance only once the step has persisted. A failed save that still let the
   * client move on would surface much later, as a refusal to finish with
   * answers missing that they know they gave.
   */
  const saveDiscoveryAndAdvance = () => {
    const payload = Object.entries(discovery)
      .filter(([, optionKeys]) => optionKeys.length > 0)
      .map(([questionId, optionKeys]) => ({ questionId, optionKeys }));

    // A question that was answered, saved, and then hidden by a changed gate
    // has a row on the server that nothing here would otherwise overwrite. It
    // is sent back empty so the stored answer is cleared too — an empty
    // selection reads as unanswered everywhere downstream.
    const cleared = Object.keys(data.savedDiscovery ?? {})
      .filter((questionId) => !(discovery[questionId]?.length > 0))
      .map((questionId) => ({ questionId, optionKeys: [] }));

    saveDiscovery.mutate(
      {
        discovery: [...payload, ...cleared],
        subjectFirstName: (isSupporter && subjectName.trim()) || null,
      },
      { onSuccess: () => setStep(0) }
    );
  };

  const savePillarAndAdvance = (index: number) => {
    const pillarAnswers = pillars[index].needs
      .filter((need) => answers[need.id] !== undefined)
      .map((need) => ({ needId: need.id, response: answers[need.id] }));
    saveAnswers.mutate(
      { answers: pillarAnswers },
      { onSuccess: () => setStep(index === pillars.length - 1 ? 'completion' : index + 1) }
    );
  };

  const node = (): ReactNode => {
    if (step === 'landing') {
      return (
        <LandingScreen
          clientName={data.clientName}
          advisorName={data.advisorName}
          onStart={() => setStep('discovery')}
          {...V2_LANDING}
        />
      );
    }
    if (step === 'discovery') {
      return (
        <V2DiscoveryScreen
          questions={data.content.discoveryQuestions}
          escalationChecks={data.content.escalationChecks}
          escalationShowWhen={data.content.escalationShowWhen}
          answers={discovery}
          onAnswer={(questionId, optionKeys) => {
            setDiscovery((prev) =>
              pruneHiddenAnswers(
                { ...prev, [questionId]: optionKeys },
                data.content.discoveryQuestions,
                data.content.escalationChecks,
                data.content.escalationShowWhen
              )
            );
            // Moving to "Me" clears the name; parent → spouse keeps it.
            if (
              questionId === 'D1' &&
              personLabelFor({ D1: optionKeys }, personLabels) === undefined
            ) {
              setSubjectName('');
            }
          }}
          subjectName={isSupporter ? subjectName : undefined}
          onSubjectNameChange={setSubjectName}
          onBack={() => setStep('landing')}
          onContinue={saveDiscoveryAndAdvance}
          isSaving={saveDiscovery.isPending}
          saveError={saveDiscovery.isError}
        />
      );
    }
    if (typeof step === 'number') {
      return (
        <V2PillarScreen
          pillar={pillars[step]}
          index={step}
          total={pillars.length}
          responseOptions={data.content.responseOptions}
          answers={answers}
          discovery={discovery}
          personLabels={personLabels}
          personOwnLabels={data.content.personOwnLabels}
          subjectName={subjectName}
          onAnswer={(needId, response) => setAnswers((prev) => ({ ...prev, [needId]: response }))}
          onBack={() => setStep(step === 0 ? 'discovery' : step - 1)}
          onContinue={() => savePillarAndAdvance(step)}
          isSaving={saveAnswers.isPending}
          saveError={saveAnswers.isError}
        />
      );
    }
    return (
      <V2CompletionScreen
        token={token}
        assessmentId={data.assessmentId}
        advisorName={data.advisorName}
        clientEmail={data.clientEmail}
        hasAccount={data.hasAccount}
        isSelfTest={data.isSelfTest}
        onCreateAccount={onCreateAccount}
        onSignIn={onSignIn}
      />
    );
  };

  return renderStep(String(step), node());
}
